import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import CountUp from "react-countup";
import { Link } from "react-router-dom";
import api, { formatCurrency, COUNTRY_FLAGS } from "@/lib/api";
import { Calculator as CalcIcon, ArrowRight, Lock, Loader2 } from "lucide-react";

export default function Calculator({ compact = false }) {
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [brandId, setBrandId] = useState("amazon");
  const [country, setCountry] = useState("US");
  const [amount, setAmount] = useState(100);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/catalog/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/catalog/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!brandId || !country || !amount || amount <= 0) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/rates/quote", {
          brand_id: brandId,
          country,
          amount: Number(amount),
        });
        setQuote(data);
      } catch {
        setQuote(null);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [brandId, country, amount]);

  const currency = useMemo(() => countries.find((c) => c.code === country)?.currency || "USD", [country, countries]);

  return (
    <section id="calculator" className={`${compact ? "" : "py-20"} bg-white`} data-testid="calculator-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!compact && (
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#00C853] font-semibold">Live Rate Calculator</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B3C5D] mt-3 tracking-tight">
              See your payout in seconds
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
              Pick a brand, country and value. Our engine calculates your guaranteed payout in
              real time — no sign-up needed.
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="bg-white rounded-3xl border border-slate-200 p-6 sm:p-8 shadow-[0_8px_32px_rgba(11,60,93,0.06)]">
            <div className="flex items-center gap-2 text-sm text-[#0B3C5D] font-semibold">
              <CalcIcon className="w-4 h-4 text-[#00C853]" /> Configure your sale
            </div>

            <div className="mt-6 space-y-5">
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Gift Card Brand</label>
                <select
                  value={brandId}
                  onChange={(e) => setBrandId(e.target.value)}
                  className="mt-2 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-900 focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                  data-testid="calc-brand-select"
                >
                  {brands.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Country</label>
                <div className="mt-2 grid grid-cols-4 gap-2">
                  {countries.map((c) => (
                    <button
                      key={c.code}
                      onClick={() => setCountry(c.code)}
                      className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                        country === c.code
                          ? "border-[#0B3C5D] bg-[#0B3C5D] text-white"
                          : "border-slate-200 text-slate-700 hover:border-[#0B3C5D]"
                      }`}
                      data-testid={`calc-country-${c.code}`}
                    >
                      <span className="mr-1">{COUNTRY_FLAGS[c.code]}</span>
                      {c.code}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-500">Card Value ({currency})</label>
                <div className="mt-2 relative">
                  <input
                    type="number"
                    min="10"
                    step="5"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white pl-4 pr-20 py-3 text-2xl font-display font-bold text-[#0B3C5D] focus:outline-none focus:border-[#0B3C5D] focus:ring-4 focus:ring-[#0B3C5D]/10"
                    data-testid="calc-amount"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-400">{currency}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[50, 100, 250, 500, 1000].map((v) => (
                    <button
                      key={v}
                      onClick={() => setAmount(v)}
                      className="px-3 py-1 rounded-full text-xs font-medium border border-slate-200 text-slate-600 hover:border-[#0B3C5D] hover:text-[#0B3C5D] transition"
                      data-testid={`calc-preset-${v}`}
                    >
                      {currency} {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Result */}
          <div className="relative">
            <div className="absolute -inset-2 bg-gradient-to-br from-[#0B3C5D]/8 to-[#00C853]/8 rounded-3xl blur-xl" />
            <div className="relative rounded-3xl bg-navy-gradient text-white p-6 sm:p-8 shadow-2xl">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-[0.2em] text-white/70">Your Payout</p>
                {loading && <Loader2 className="w-4 h-4 animate-spin text-[#00C853]" />}
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={quote ? `${quote.final_payout}-${quote.currency}` : "none"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="mt-2"
                >
                  <p className="font-display text-5xl sm:text-6xl font-bold tracking-tight" data-testid="calc-payout">
                    {quote ? (
                      <>
                        <span className="text-[#00C853]">
                          {quote.currency === "USD" ? "$" : quote.currency === "GBP" ? "£" : quote.currency === "CAD" ? "C$" : "د.إ"}
                        </span>
                        <CountUp
                          end={quote.final_payout}
                          duration={0.6}
                          separator=","
                          decimals={2}
                          preserveValue
                        />
                      </>
                    ) : (
                      "—"
                    )}
                  </p>
                </motion.div>
              </AnimatePresence>

              <div className="mt-6 space-y-3 text-sm">
                <Row label="Exchange Rate" value={quote ? `${(quote.rate_pct * 100).toFixed(1)}%` : "—"} />
                <Row label="Gross Payout" value={quote ? formatCurrency(quote.gross_payout, quote.currency) : "—"} />
                <Row label="Processing Fee" value={quote ? `- ${formatCurrency(quote.processing_fee, quote.currency)}` : "—"} />
                <div className="border-t border-white/10 pt-3 flex items-center justify-between text-white">
                  <span className="font-semibold">Final Payout</span>
                  <span className="font-display font-bold text-[#00C853]" data-testid="calc-final">
                    {quote ? formatCurrency(quote.final_payout, quote.currency) : "—"}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-2 text-xs text-white/70">
                <Lock className="w-3.5 h-3.5 text-[#00C853]" /> Rate locked for 5 minutes after submission
              </div>

              <Link
                to="/sell"
                className="mt-6 inline-flex items-center justify-center w-full rounded-full bg-[#00C853] text-white py-3.5 font-semibold hover:bg-[#00a543] transition"
                data-testid="calc-sell-cta"
              >
                Lock my rate & sell
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between text-white/80">
      <span>{label}</span>
      <span className="font-medium text-white">{value}</span>
    </div>
  );
}
